import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Space,
  Typography,
  Spin,
  Alert,
  Popconfirm,
  Tooltip,
  DatePicker,
  message,
  Upload,
  theme,
  Grid,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UserOutlined,
  CreditCardOutlined,
  SearchOutlined,
  ReloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { adminService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Title } = Typography;
const { Option } = Select;


const AdminUsers = () => {
  const { token } = theme.useToken();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const screens = Grid.useBreakpoint();
  const esMovil = !screens.md;
  const [selectedUser, setSelectedUser] = useState(null);
  const [captureModalVisible, setCaptureModalVisible] = useState(false);
  const [captureUploading, setCaptureUploading] = useState(false);
  const [captureUser, setCaptureUser] = useState(null);
  const [error, setError] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} usuarios`,
  });
  const [form] = Form.useForm();
  const [captureForm] = Form.useForm();

  // Debounce para la búsqueda
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchData = async (page = 1, search = '') => {
    try {
      setSearchLoading(true);
      const offset = (page - 1) * pagination.pageSize;
      const response = await adminService.getUsers(page, search);
      
      // Extraer los usuarios del objeto de respuesta
      const usersData = response?.data?.users || response?.users || response || [];
      const paginationData = response?.data?.pagination || {};
      
      setUsers(usersData);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: paginationData.total || usersData.length,
      }));
    } catch (error) {
      setError('Error al cargar los usuarios');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const plansResponse = await adminService.getPlans();
      const plansData = plansResponse?.data || plansResponse || [];
      setPlans(plansData);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // Función debounced para búsqueda
  const debouncedSearch = useCallback((value) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      fetchData(1, value);
    }, 500); // Esperar 500ms después de que el usuario deje de escribir
    
    setSearchTimeout(timeout);
  }, [searchTimeout]);

  const handleSearch = (value) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    // Si el campo está vacío, buscar inmediatamente
    if (value === '') {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      fetchData(1, '');
    } else {
      debouncedSearch(value);
    }
  };

  const handleTableChange = (paginationConfig) => {
    fetchData(paginationConfig.current, searchValue);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    form.setFieldsValue({
      ...user,
      password: '', // No mostrar la contraseña actual
      plan_renewal_date: user.plan_renewal_date ? dayjs(user.plan_renewal_date) : null
    });
    setModalVisible(true);
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    form.resetFields();
    form.setFieldsValue({
      username: '',
      name: '',
      email: '',
      password: '',
      plan_id: '',
      bulk_credits: 0,
      is_active: true
    });
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedUser(null);
    form.resetFields();
  };

  const handleSaveUser = async (values) => {
    try {
      // Formatear fecha de renovación si existe
      const formattedValues = { ...values };
      if (formattedValues.plan_renewal_date) {
        formattedValues.plan_renewal_date = formattedValues.plan_renewal_date.format('YYYY-MM-DD');
      }

      if (selectedUser?.id) {
        // Si no se proporciona contraseña, no la incluimos en la actualización
        if (!formattedValues.password) {
          delete formattedValues.password;
        }
        await adminService.updateUser(selectedUser.id, formattedValues);
      } else {
        await adminService.createUser(formattedValues);
      }
      await fetchData(pagination.current, searchValue);
      handleCloseModal();
    } catch (error) {
      setError('Error al guardar el usuario');
      console.error('Error saving user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminService.deleteUser(userId);
      await fetchData(pagination.current, searchValue);
    } catch (error) {
      setError('Error al eliminar el usuario');
      console.error('Error deleting user:', error);
    }
  };

  const handleAddCredits = async (userId, credits) => {
    try {
      await adminService.addCredits(userId, credits);
      await fetchData(pagination.current, searchValue);
    } catch (error) {
      setError('Error al agregar créditos');
      console.error('Error adding credits:', error);
    }
  };

  const handleRegenerateInvoiceTaxes = async (user) => {
    try {
      const hide = message.loading('Regenerando impuestos de facturas para este usuario...', 0);
      await adminService.regenerateInvoiceTaxesForUser(user.id);
      hide();
      message.success('Impuestos de facturas regenerados correctamente para el usuario');
    } catch (error) {
      console.error('Error regenerating invoice taxes for user:', error);
      message.error('Error al regenerar los impuestos de facturas para el usuario');
    }
  };

  const showAddCreditsModal = (user) => {
    Modal.confirm({
      title: `Agregar créditos a ${user.email}`,
      content: (
        <div>
          <p>Créditos actuales: {user.total_credits ?? ((user.plan_credits || 0) - (user.plan_credits_used || 0) + (user.bulk_credits || 0))}</p>
          <Input
            placeholder="Cantidad de créditos a agregar"
            type="number"
            id="credits-input"
          />
        </div>
      ),
      onOk: () => {
        const input = document.getElementById('credits-input');
        const credits = parseInt(input.value);
        if (credits && !isNaN(credits)) {
          handleAddCredits(user.id, credits);
        }
      },
    });
  };

  const handleToggleStatus = async (user, checked) => {
    try {
      await adminService.toggleUserStatus(user.id, checked);
      // Actualizar la lista localmente para reflejar el cambio rápido
      setUsers(users.map(u => 
        u.id === user.id ? { ...u, is_active: checked } : u
      ));
    } catch (error) {
      setError('Error al cambiar el estado del usuario');
      console.error('Error toggling status:', error);
      // Revertir cambio si falla (recargar datos)
      fetchData(pagination.current, searchValue);
    }
  };

  const openCaptureModal = (user) => {
    setCaptureUser(user);
    setCaptureModalVisible(true);
    captureForm.resetFields();
    captureForm.setFieldsValue({
      rfc: '',
      amount: undefined,
      period: dayjs().startOf('month'),
    });
  };

  const closeCaptureModal = () => {
    setCaptureModalVisible(false);
    setCaptureUser(null);
    captureForm.resetFields();
  };

  const handleUploadCaptureLine = async (values) => {
    if (!captureUser?.id) return;
    const pdfFile = values.pdf?.fileList?.[0]?.originFileObj;
    if (!pdfFile) {
      message.error('Selecciona un PDF');
      return;
    }

    try {
      setCaptureUploading(true);
      const formData = new FormData();
      formData.append('user_id', captureUser.id);
      formData.append('rfc', values.rfc);
      formData.append('amount', String(values.amount));
      formData.append('period', values.period.format('YYYY-MM'));
      // Versión del acuse: 0 = declaración normal, 1..N = complementarias.
      formData.append('consecutivo', String(values.consecutivo ?? 0));
      formData.append('pdf', pdfFile);

      await adminService.uploadCaptureLine(formData);
      message.success('Línea de captura cargada correctamente');
      closeCaptureModal();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Error al cargar la línea de captura');
    } finally {
      setCaptureUploading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Usuario',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          checkedChildren="Activo"
          unCheckedChildren="Inactivo"
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'subscription_plan',
      key: 'subscription_plan',
      render: (plan) => plan?.name || 'Sin plan',
    },
    {
      title: 'Vigencia Plan',
      dataIndex: 'plan_renewal_date',
      key: 'plan_renewal_date',
      width: 120,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'Créditos Disponibles',
      key: 'credits',
      render: (_, record) => {
        const availableCredits = record.total_credits ?? ((record.plan_credits || 0) - (record.plan_credits_used || 0) + (record.bulk_credits || 0));
        return (
          <Space>
            <span>{availableCredits}</span>
            <Tooltip title="Agregar créditos">
              <Button
                type="text"
                size="small"
                icon={<CreditCardOutlined />}
                onClick={() => showAddCreditsModal(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        // `wrap`: son cinco acciones y en una sola fila se salen del ancho del
        // teléfono, donde la tarjeta ya no tiene scroll horizontal.
        <Space wrap>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditUser(record)}
          >
            Editar
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleRegenerateInvoiceTaxes(record)}
          >
            Regenerar impuestos
          </Button>
          <Button
            size="small"
            icon={<UploadOutlined />}
            onClick={() => openCaptureModal(record)}
          >
            Línea de captura
          </Button>
          <Popconfirm
            title="¿Estás seguro de que quieres eliminar este usuario?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: esMovil ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: esMovil ? 'stretch' : 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Space align="center">
          <UserOutlined style={{ fontSize: esMovil ? 20 : 24 }} />
          <Title level={esMovil ? 4 : 2} style={{ margin: 0 }}>
            Gestión de Usuarios
          </Title>
        </Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNewUser}
          block={esMovil}
          size={esMovil ? 'large' : 'middle'}
        >
          Nuevo Usuario
        </Button>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Buscador */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: esMovil ? 'column' : 'row',
            gap: '8px',
            maxWidth: esMovil ? '100%' : 500,
          }}
        >
          <Input
            placeholder="Buscar por nombre o email..."
            allowClear
            size="large"
            value={searchValue}
            onChange={handleSearchChange}
            onPressEnter={(e) => handleSearch(e.target.value)}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="large"
            onClick={() => handleSearch(searchValue)}
            loading={searchLoading}
            block={esMovil}
          >
            Buscar
          </Button>
        </div>
        <div style={{ marginTop: 8, color: token.colorTextSecondary }}>
          {searchValue && `Resultados para: "${searchValue}"`}
        </div>
      </div>

      <ResponsiveTable
        columns={columns}
        dataSource={users}
        rowKey="id"
        pagination={pagination}
        onChange={handleTableChange}
        loading={searchLoading}
      />

      {/* Modal para editar/crear usuario */}
      <Modal
        title={selectedUser?.id ? 'Editar Usuario' : 'Nuevo Usuario'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={esMovil ? '100%' : 600}
        style={esMovil ? { top: 0, maxWidth: '100vw', margin: 0 } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveUser}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Por favor ingresa el email' },
              { type: 'email', message: 'Por favor ingresa un email válido' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Nombre completo"
            rules={[{ required: true, message: 'Por favor ingresa el nombre completo' }]}
          >
            <Input />
          </Form.Item>

          {!selectedUser?.id && (
            <Form.Item
              name="password"
              label="Contraseña"
              rules={[{ required: true, message: 'Por favor ingresa la contraseña' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          {selectedUser?.id && (
            <Form.Item
              name="password"
              label="Nueva contraseña (opcional)"
            >
              <Input.Password placeholder="Dejar vacío para mantener la actual" />
            </Form.Item>
          )}

          <Form.Item
            name="plan_id"
            label="Plan"
          >
            <Select placeholder="Seleccionar plan" allowClear>
              {plans.map((plan) => (
                <Option key={plan.id} value={plan.id}>
                  {plan.name} - {plan.monthly_credits} créditos
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="plan_renewal_date"
            label="Vigencia del Plan"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              placeholder="Seleccionar fecha de expiración" 
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item
            name="bulk_credits"
            label="Créditos adicionales"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Estado"
            valuePropName="checked"
          >
            <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Guardar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Cargar línea de captura${captureUser?.email ? ` — ${captureUser.email}` : ''}`}
        open={captureModalVisible}
        onCancel={closeCaptureModal}
        footer={null}
        width={esMovil ? '100%' : 600}
        style={esMovil ? { top: 0, maxWidth: '100vw', margin: 0 } : undefined}
      >
        <Form
          form={captureForm}
          layout="vertical"
          onFinish={handleUploadCaptureLine}
        >
          <Form.Item
            name="rfc"
            label="RFC"
            rules={[
              { required: true, message: 'RFC requerido' },
              { pattern: /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/i, message: 'RFC inválido' },
            ]}
          >
            <Input placeholder="RFC de la empresa (tax_id)" />
          </Form.Item>

          <Form.Item
            name="period"
            label="Periodo (YYYY-MM)"
            rules={[{ required: true, message: 'Periodo requerido' }]}
          >
            <DatePicker picker="month" format="YYYY-MM" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Monto a pagar"
            rules={[{ required: true, message: 'Monto requerido' }]}
          >
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          {/* Esta pantalla captura el RFC a mano y no consulta los acuses de la
              empresa, así que no puede proponer la versión: se indica explícita.
              El panel fiscal sí la sugiere. */}
          <Form.Item
            name="consecutivo"
            label="Versión"
            initialValue={0}
            rules={[{ required: true, message: 'Versión requerida' }]}
            extra="0 es la declaración normal; 1 en adelante son complementarias del mismo periodo. Ninguna reemplaza a la anterior."
          >
            <InputNumber min={0} max={99} step={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="pdf"
            label="PDF"
            rules={[{ required: true, message: 'PDF requerido' }]}
          >
            <Upload.Dragger
              beforeUpload={() => false}
              accept="application/pdf,.pdf"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Haz clic o arrastra el PDF aquí</p>
              <p className="ant-upload-hint">Solo archivos .pdf</p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeCaptureModal}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={captureUploading}>
                Cargar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
